import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { isIOSSafari } from '@/hooks/use-ui-visual-viewport';
import { ChatMessage } from './chat-message';
import { ChatLiveAnnouncer } from './chat-live-announcer';
import { AgentActivityTimeline } from './agent-activity-timeline';
import { MemoryActivityCluster } from './memory-activity-cluster';
import {
  activeStreamingRunId,
  latestVoiceMessageIdForRun,
} from '@/threading/turn-order';
import { useAuth } from '@/hooks/use-auth';
import { useProfile } from '@/hooks/use-profile';
import { getProfileInitials } from '@/components/profile/profile-initials';
import { userPersonNameFromAuth } from '@/lib/format-display-name';
import { getChatMessageStyle, getChatUserAvatarStyle } from '@/components/chat/chat-message-styles';
import { CHAT_THREAD_NEAR_BOTTOM_PX } from '@/hooks/use-chat-thread-scroll-edges';
import type { ChatMessage as ChatMessageType } from '../../types';

type RenderItem =
  | { kind: 'message'; message: ChatMessageType }
  | { kind: 'toolGroup'; key: string; runId?: string; messages: ChatMessageType[] }
  | { kind: 'memoryGroup'; key: string; runId?: string; memorySubType?: 'retrieved' | 'stored'; messages: ChatMessageType[] };

/**
 * Collapse consecutive tool delta messages sharing a runId into one
 * activity-timeline group; consecutive memory deltas by runId + subType
 * into memory clusters; everything else renders as-is.
 */
const groupActivityDeltas = (messages: ChatMessageType[]): RenderItem[] => {
  const items: RenderItem[] = [];
  for (const message of messages) {
    const isToolDelta = !message.isUser && message.isDelta && message.deltaType === 'tool';
    const isMemoryDelta = !message.isUser && message.isDelta && message.deltaType === 'memory';
    const last = items[items.length - 1];

    if (isToolDelta && last?.kind === 'toolGroup' && last.runId === message.runId) {
      last.messages.push(message);
    } else if (isToolDelta) {
      items.push({
        kind: 'toolGroup',
        key: `toolgroup_${message.id}`,
        runId: message.runId,
        messages: [message],
      });
    } else if (
      isMemoryDelta &&
      last?.kind === 'memoryGroup' &&
      last.runId === message.runId &&
      last.memorySubType === message.memorySubType
    ) {
      last.messages.push(message);
    } else if (isMemoryDelta) {
      items.push({
        kind: 'memoryGroup',
        key: `memorygroup_${message.id}`,
        runId: message.runId,
        memorySubType: message.memorySubType,
        messages: [message],
      });
    } else {
      items.push({ kind: 'message', message });
    }
  }
  return items;
};

interface ChatMessagesProps {
  messages: ChatMessageType[];
  /** True while a turn is streaming — keeps the active tool stack expanded and drives the caret. */
  isStreaming?: boolean;
  onFeedback?: (runId: string, score?: number, tags?: string[], comment?: string) => void;
  onOpenNavigationIntent?: (messageId: string) => void;
  onCancelNavigationIntent?: (messageId: string) => void;
  onSendSuggestion?: (message: string) => void;
  chatSuggestionsMaxPills?: number;
  chatSuggestionsDisabled?: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

/** Memoized: skips the whole message tree when props are unchanged (e.g. composer keystrokes). */
export const ChatMessages = memo(function ChatMessages({
  messages,
  isStreaming = false,
  onFeedback,
  onOpenNavigationIntent,
  onCancelNavigationIntent,
  onSendSuggestion,
  chatSuggestionsMaxPills,
  chatSuggestionsDisabled,
  scrollContainerRef,
}: ChatMessagesProps) {
  const chatMessageStyle = getChatMessageStyle();
  const isClassicStyle = chatMessageStyle === 'classic';
  const chatUserAvatarStyle = getChatUserAvatarStyle();
  const useProfileAvatar = isClassicStyle && chatUserAvatarStyle === 'profile';
  const { user } = useAuth();
  const { profile } = useProfile();
  const userAvatarMeta = useMemo(() => {
    if (!useProfileAvatar) return null;
    return getProfileInitials({
      first_name: user?.first_name || profile?.first_name,
      last_name: user?.last_name || profile?.last_name,
      full_name: userPersonNameFromAuth(user) || profile?.full_name,
      email: user?.email || profile?.email,
      id: String(profile?.id ?? user?.id ?? user?.email ?? 'profile'),
      avatar_url: profile?.avatar_url,
    });
  }, [useProfileAvatar, user, profile]);

  const renderItems = useMemo(() => groupActivityDeltas(messages), [messages]);
  const announcerMessages = useMemo(() => {
    const items: Array<{ id: string; role: string; text: string }> = [];
    for (const item of renderItems) {
      if (item.kind !== 'message') continue;
      const { message } = item;
      if (message.isDelta || message.messageType) continue;
      items.push({
        id: message.id,
        role: message.isUser ? 'user' : 'assistant',
        text: message.content ?? '',
      });
    }
    return items;
  }, [renderItems]);
  const { lastToolGroupKey, lastMemoryGroupKey } = useMemo(() => {
    let toolKey: string | null = null;
    let memoryKey: string | null = null;
    for (let i = renderItems.length - 1; i >= 0; i--) {
      const item = renderItems[i];
      if (!toolKey && item.kind === 'toolGroup') toolKey = item.key;
      if (!memoryKey && item.kind === 'memoryGroup') memoryKey = item.key;
      if (toolKey && memoryKey) break;
    }
    return { lastToolGroupKey: toolKey, lastMemoryGroupKey: memoryKey };
  }, [renderItems]);
  const streamingMessageId = useMemo(() => {
    if (!isStreaming) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!m.isUser && !m.isDelta && !m.messageType && m.content?.trim()) return m.id;
    }
    return null;
  }, [isStreaming, messages]);
  const streamingRunId = useMemo(
    () => activeStreamingRunId(messages, isStreaming),
    [isStreaming, messages],
  );
  const latestVoiceMessageId = useMemo(
    () => latestVoiceMessageIdForRun(messages, streamingRunId),
    [messages, streamingRunId],
  );
  const pinnedToBottomRef = useRef(true);
  const scrollFrameRef = useRef<number | null>(null);

  const getScrollContainer = useCallback(() => scrollContainerRef.current, [scrollContainerRef]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = getScrollContainer();
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior,
    });
  }, [getScrollContainer]);

  // Track whether the user is pinned to the bottom; only auto-scroll when pinned
  // so reading scrollback during a stream is never hijacked.
  useEffect(() => {
    const el = getScrollContainer();
    if (!el) return;
    const onScroll = () => {
      pinnedToBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < CHAT_THREAD_NEAR_BOTTOM_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [getScrollContainer]);

  // Auto-scroll on new content, coalesced per animation frame.
  useEffect(() => {
    if (!pinnedToBottomRef.current) return;
    if (scrollFrameRef.current !== null) return;
    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      if (pinnedToBottomRef.current) {
        scrollToBottom(isStreaming ? 'auto' : 'smooth');
      }
    });
    return () => {
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [isStreaming, messages, scrollToBottom]);

  // Mobile (non–iOS Safari): keep bottom in view when the keyboard resizes the visual viewport.
  // iOS Safari: skip — keyboard padding reflows scrollHeight on every visualViewport tick; forcing
  // scrollTop = scrollHeight each time caused severe jitter.
  useEffect(() => {
    const el = getScrollContainer();
    if (!el) return;
    if (isIOSSafari()) return;

    const vv = window.visualViewport;
    if (!vv) return;

    const nearBottom = (thresholdPx = 120) =>
      el.scrollHeight - el.scrollTop - el.clientHeight < thresholdPx;

    const onVV = () => {
      if (!nearBottom()) return;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    };

    vv.addEventListener('resize', onVV);
    vv.addEventListener('scroll', onVV);
    return () => {
      vv.removeEventListener('resize', onVV);
      vv.removeEventListener('scroll', onVV);
    };
  }, [getScrollContainer]);

  return (
    <div className="space-y-4">
        <ChatLiveAnnouncer
          messages={announcerMessages}
          streamingMessageId={streamingMessageId}
          isStreaming={isStreaming}
        />
        {renderItems.map(item => {
          if (item.kind === 'toolGroup') {
            return (
              <AgentActivityTimeline
                key={item.key}
                messages={item.messages}
                live={isStreaming && item.key === lastToolGroupKey}
              />
            );
          }
          if (item.kind === 'memoryGroup') {
            return (
              <MemoryActivityCluster
                key={item.key}
                messages={item.messages}
                live={isStreaming && item.key === lastMemoryGroupKey}
              />
            );
          }
          return (
            <ChatMessage
              key={item.message.id}
              message={item.message}
              streaming={item.message.id === streamingMessageId}
              voiceShimmerActive={
                isStreaming &&
                item.message.isDelta === true &&
                item.message.deltaType === 'voice' &&
                item.message.id === latestVoiceMessageId
              }
              onFeedback={onFeedback}
              onOpenNavigationIntent={onOpenNavigationIntent}
              onCancelNavigationIntent={onCancelNavigationIntent}
              onSendSuggestion={onSendSuggestion}
              chatSuggestionsMaxPills={chatSuggestionsMaxPills}
              chatSuggestionsDisabled={chatSuggestionsDisabled}
              userAvatarMode={isClassicStyle ? chatUserAvatarStyle : 'icon'}
              userAvatarUrl={useProfileAvatar ? profile?.avatar_url : undefined}
              userAvatarColorSeed={useProfileAvatar ? userAvatarMeta?.colorSeed : undefined}
            />
          );
        })}
    </div>
  );
});
